import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { removeFile } from "../lib/storage.js";

/**
 * Condition reports, plus their photos in the separate `condition_photos`
 * table. v10 kept photos on the report record; the schema normalises them, so
 * saving a report means reconciling two tables.
 */

export function useReports() {
  return useQuery({
    queryKey: ["condition_reports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("condition_reports")
        .select(
          "id,rental_id,equipment_id,phase,date,notes," +
            "equipment(name),condition_photos(id,file_path)," +
            "rentals(start_date,customers(name))"
        )
        .order("date", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useRentalOptions() {
  return useQuery({
    queryKey: ["rentals", "report-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rentals")
        .select("id,equipment_id,start_date,customers(name)")
        .order("start_date", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useSaveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, form, previousPhotos = [] }) => {
      const row = {
        equipment_id: form.equipmentId,
        rental_id: form.rentalId || null,
        phase: form.phase,
        date: form.date,
        notes: form.notes || null,
      };

      const { data, error } = id
        ? await supabase.from("condition_reports").update(row).eq("id", id).select("id")
        : await supabase.from("condition_reports").insert([row]).select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) {
        throw new Error("The database accepted the request but changed nothing — usually a security policy.");
      }
      const reportId = data[0].id;

      // Reconcile photos: insert the new paths, delete the rows and files for
      // any that were removed. Done after the report row is saved so a
      // cancelled edit never destroys a photo.
      const keptPaths = new Set(form.photos.map((p) => p.path));
      const previousPaths = new Set(previousPhotos.map((p) => p.file_path));

      const toAdd = form.photos.filter((p) => !previousPaths.has(p.path));
      if (toAdd.length) {
        const { error: addErr } = await supabase
          .from("condition_photos")
          .insert(toAdd.map((p) => ({ report_id: reportId, file_path: p.path })));
        if (addErr) throw new Error(`Report saved, but a photo could not be attached: ${addErr.message}`);
      }

      const toRemove = previousPhotos.filter((p) => !keptPaths.has(p.file_path));
      if (toRemove.length) {
        await supabase.from("condition_photos").delete().in("id", toRemove.map((p) => p.id));
        for (const p of toRemove) await removeFile(p.file_path);
      }

      return { id: reportId };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["condition_reports"] }),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, photos = [] }) => {
      // condition_photos cascades on delete, so only the stored files need
      // clearing up explicitly.
      const { data, error } = await supabase
        .from("condition_reports")
        .delete()
        .eq("id", id)
        .select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error("Nothing was deleted — a security policy may have blocked it.");
      for (const p of photos) await removeFile(p.file_path);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["condition_reports"] }),
  });
}
